import { Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Linkedin, Mail, Code2, Palette, FolderKanban } from "lucide-react";

const leadership = [
  {
    name: "Kata Naga Venkatesh",
    role: "Director & Chairperson",
    skills: "Full Stack Development",
    icon: Code2,
    description: "Visionary technologist with deep expertise in full-stack development. Leads the company's strategic direction and technical architecture, ensuring every project meets the highest standards of engineering excellence.",
    specialties: ["React", "Node.js", "Python", "System Architecture", "Cloud Infrastructure", "Team Leadership"],
  },
  {
    name: "Sriram Diddi",
    role: "Director & Front End Designer",
    skills: "Frontend Design & Development",
    icon: Palette,
    description: "Creative force behind Scriptovative's design philosophy. Specializes in crafting intuitive, visually compelling user interfaces that drive engagement and deliver exceptional user experiences.",
    specialties: ["UI/UX Design", "React", "CSS/Tailwind", "Figma", "Responsive Design", "Design Systems"],
  },
  {
    name: "Santhosh Munagala",
    role: "Project Manager & Full Stack Developer",
    skills: "Project Management & Full Stack Development",
    icon: FolderKanban,
    description: "Bridges the gap between technical execution and project delivery. Combines hands-on development expertise with strong project management skills to ensure on-time, on-budget delivery.",
    specialties: ["Agile/Scrum", "Full Stack Dev", "React", "Node.js", "Client Management", "DevOps"],
  },
];

export default function Team() {
  return (
    <div data-testid="team-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/3 right-1/3 w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10 text-xs font-mono" data-testid="team-badge">
              OUR TEAM
            </Badge>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 leading-[1.1] tracking-tight">
              The people behind the{" "}
              <span className="gradient-text">code.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-400 leading-relaxed max-w-xl">
              A passionate team of developers, designers, and project managers 
              dedicated to engineering digital excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="relative py-24 lg:py-32" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/20 hover:bg-cyan-500/10 text-xs font-mono">
              LEADERSHIP
            </Badge>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
              Meet our <span className="gradient-text">leaders.</span>
            </h2>
          </div>

          <div className="space-y-8">
            {leadership.map((member, i) => (
              <div
                key={i}
                data-testid={`team-member-${i}`}
                className="grid lg:grid-cols-3 gap-8 p-8 lg:p-10 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-indigo-500/20 transition-all group"
              >
                <div className="lg:col-span-1">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center group-hover:bg-indigo-500/20 transition-colors">
                      <member.icon className="w-8 h-8 text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-['Outfit'] text-xl font-bold text-slate-50">{member.name}</h3>
                      <p className="text-indigo-400 text-sm font-medium">{member.role}</p>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed">{member.description}</p>
                </div>

                <div className="lg:col-span-2">
                  <h4 className="font-['Outfit'] text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
                    Core Expertise
                  </h4>
                  <p className="text-slate-400 text-sm mb-6">{member.skills}</p>
                  <div className="flex flex-wrap gap-2">
                    {member.specialties.map((s) => (
                      <span
                        key={s}
                        className="px-3 py-1.5 rounded-full bg-slate-800/80 text-slate-300 text-xs font-mono border border-slate-700/50 hover:border-indigo-500/30 transition-colors"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="relative py-24 lg:py-32 bg-slate-900/30" data-testid="culture-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10 text-xs font-mono">
                OUR CULTURE
              </Badge>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight mb-6">
                Where innovation meets <span className="gradient-text">collaboration.</span>
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-4">
                At Scriptovative, we believe the best software is built by teams that 
                communicate openly, challenge each other respectfully, and celebrate 
                every win together.
              </p>
              <p className="text-slate-400 text-sm leading-relaxed mb-8">
                Our flat organizational structure ensures everyone's voice is heard, 
                and our commitment to continuous learning keeps us at the cutting edge 
                of technology.
              </p>
              <Link
                to="/careers"
                data-testid="team-cta-careers"
                className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 font-medium transition-colors text-sm"
              >
                View open positions
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-slate-800">
                <img
                  src="https://images.pexels.com/photos/1181405/pexels-photo-1181405.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Team collaboration"
                  className="w-full h-80 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-t from-indigo-600/5 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
            Want to work with <span className="gradient-text">us?</span>
          </h2>
          <p className="mt-4 text-base text-slate-400">
            Whether as a client or a team member, we'd love to connect.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="team-cta-contact" className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:-translate-y-0.5">
              Start a Project
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/careers" data-testid="team-cta-join" className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 hover:bg-slate-800/50 px-8 py-3.5 rounded-full font-medium transition-all hover:-translate-y-0.5">
              Join Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
